import dayjs from 'dayjs';

import type { AclosetMappingGroupTypeCode } from '@/MetadataType';

import type {
  입고_예정서_등록_요청_타입,
  입고_예정서_목록_조회_요청_타입,
  입고_패키지_수정_요청_타입,
  패키지_목록_조회_요청_타입,
} from './types';

type SelectOption<T> = { label: string; value: T };

/**
 * 코너로지스 API 통신할 때 사용하는 날짜 포맷
 */
export const 코너로지스_날짜_포맷 = 'YYYY-MM-DD';

/**
 * 코너로지스 API 통신할 때 사용하는 시간 포맷
 */
export const 코너로지스_시간_포맷 = 'HH:mm:ss';

/**
 * 코너로지스 패키지 생성 가능한 최대 개수
 */
export const MAX_PACKAGE_COUNT = 200;

/**
 * 입고 예정서 등록/수정 요청 - 입고 타입 리스트
 */
export const 입고_타입_리스트: Array<SelectOption<입고_예정서_등록_요청_타입[number]['inboundType']>> = [
  { label: '일반', value: 'DEFAULT' },
  { label: '재고이관', value: 'MOVE' },
];

/**
 * 입고 예정서 등록/수정 요청 - 운송 방식 리스트
 */
export const 운송_방식_리스트: Array<SelectOption<입고_예정서_등록_요청_타입[number]['transportMethod']>> = [
  { label: '직접 배송', value: 'DIRECT' },
  { label: '택배 배송', value: 'DELIVERY' },
  { label: '화물 배송', value: 'FREIGHT' },
  { label: '퀵 배송', value: 'QUICK' },
];

/**
 * 입고 예정서 목록 필터 리스트
 */
export const 입고_예정서_목록_필터_리스트: Array<SelectOption<입고_예정서_목록_조회_요청_타입['status']>> = [
  { label: '전체보기', value: undefined },
  { label: '입고대기', value: 'S' },
  { label: '입고처리중', value: 'I' },
  { label: '입고완료', value: 'E' },
];

type CategoryCodeType = NonNullable<입고_패키지_수정_요청_타입['body']['category']>;

/**
 * 코너로지스에서 관리하는 성별 리스트
 */
export const 성별_리스트: Array<SelectOption<CategoryCodeType> & { mappingGroupType: AclosetMappingGroupTypeCode }> = [
  { label: '여성', value: 'F', mappingGroupType: 'MAPPING-GROUP_TYPE_WOMEN' },
  { label: '남성', value: 'M', mappingGroupType: 'MAPPING-GROUP_TYPE_MEN' },
  { label: '유니섹스', value: 'U', mappingGroupType: 'MAPPING-GROUP_TYPE_UNISEX' },
  { label: '키즈 여', value: 'G', mappingGroupType: 'MAPPING-GROUP_TYPE_KIDS-WOMEN' },
  { label: '키즈 남', value: 'B', mappingGroupType: 'MAPPING-GROUP_TYPE_KIDS-MEN' },
  { label: '키즈', value: 'C', mappingGroupType: 'MAPPING-GROUP_TYPE_KIDS-UNISEX' },
];

/**
 * 상품화 진행상태 필터 리스트
 */
export const 상품화_진행상태_필터_리스트: Array<SelectOption<패키지_목록_조회_요청_타입['status']>> = [
  { label: '전체보기', value: undefined },
  { label: '상품화 전', value: 'WAITING' },
  { label: '상품화 진행중', value: 'IN_PROGRESS' },
  { label: '상품화 완료', value: 'COMPLETED' },
];

/**
 * 코너로지스 주문 목록 조회 - 처리 상태 리스트
 */
export const 코너로지스_주문_처리_상태_리스트 = [
  'RECEIVING_SHIPMENTS', // 출고 준비 중
  'PROGRESSING_SHIPMENTS', // 출고 진행 중
  'COMPLETED_SHIPMENTS', // 출고 완료
  'CANCEL_SHIPMENTS', // 출고 요청 취소
  'RECEIVING_RETURN', // 반품 준비 중
  'PROGRESSING_RETURN', // 반품 진행 중
  'COMPLETED_RETURN', //반품 완료
] as const;

/**
 * 코너로지스 주문 목록 조회 - 처리 상태 필터 리스트
 */
export const 코너로지스_주문_처리_상태_필터_리스트: Array<
  SelectOption<(typeof 코너로지스_주문_처리_상태_리스트)[number]>
> = [
  { label: '출고 확인 전', value: 'RECEIVING_SHIPMENTS' },
  { label: '출고 처리중', value: 'PROGRESSING_SHIPMENTS' },
  { label: '출고 완료', value: 'COMPLETED_SHIPMENTS' },
  { label: '출고 취소', value: 'CANCEL_SHIPMENTS' },
  { label: '반품 준비중', value: 'RECEIVING_RETURN' },
  { label: '반품 진행중', value: 'PROGRESSING_RETURN' },
  { label: '반품 완료', value: 'COMPLETED_RETURN' },
];

/**
 * 코너로지스 기간 필터 리스트
 */
export const 코너로지스_기간_필터_리스트: Array<SelectOption<dayjs.Dayjs | undefined>> = [
  { label: '오늘', value: dayjs().endOf('day') },
  { label: '1주일', value: dayjs().subtract(7, 'day').endOf('day') },
  { label: '1개월', value: dayjs().subtract(1, 'month').endOf('day') },
  { label: '3개월', value: dayjs().subtract(3, 'month').endOf('day') },
  { label: '6개월', value: dayjs().subtract(6, 'month').endOf('day') },
  { label: '직접입력', value: dayjs().endOf('day') },
];

/**
 * 코너로지스 주문 목록 조회 - 기간 검색 조건 리스트
 */
export const 코너로지스_주문_목록_기간_검색_조건_리스트 = [
  'RETURN', // 반품 요청일
] as const;

/**
 * 코너로지스 주문 목록 조회 - 기간 검색 조건 필터 리스트
 */
export const 코너로지스_주문_목록_기간_검색_조건_필터_리스트: Array<
  SelectOption<(typeof 코너로지스_주문_목록_기간_검색_조건_리스트)[number] | 'all'>
> = [
  { label: '출고 요청일', value: 'all' },
  { label: '반품 요청일', value: 'RETURN' },
];

/**
 * 코너로지스 주문 출고 등록 모달 - 쇼핑몰 드롭다운 리스트
 */
export const 코너로지스_주문_출고_등록_쇼핑몰_리스트 = [
  { id: '세컨솔드', name: '세컨솔드' },
  { id: '카페24', name: '카페24' },
  { id: '네이버', name: '네이버' },
  { id: '인스타그램', name: '인스타그램' },
  { id: '번개장터', name: '번개장터' },
  { id: '기타', name: '기타' },
];

/**
 * 코너로지스 주문 출고 등록 모달에서
 * 쇼핑몰 주문번호 유효성 검사하는 쇼핑몰 리스트
 */
export const 주문_출고_쇼핑몰_유효성_검사_리스트 = ['세컨솔드', '카페24', '네이버'];

/**
 * 코너로지스 상품 폐기 상태 리스트
 */
export const 상품_폐기_상태_리스트 = ['WAITING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;

/**
 * 코너로지스 상품 폐기 상태 필터 리스트
 */
export const 상품_폐기_상태_필터_리스트: Array<SelectOption<(typeof 상품_폐기_상태_리스트)[number]>> = [
  { label: '폐기 대기 중', value: 'WAITING' },
  { label: '진행 중', value: 'IN_PROGRESS' },
  { label: '진행 완료', value: 'COMPLETED' },
  { label: '폐기 취소', value: 'CANCELLED' },
];

/**
 * 코너로지스 상품 반출 상태 리스트
 */
export const 상품_반출_상태_리스트 = [
  'PENDING_PAYMENT', // 결제 대기
  'PAYMENT_COMPLETED', // 결제 완료
  'IN_PROGRESS', // 진행 중
  'COMPLETED', // 진행 완료
  'CANCELLED', //반출 취소
] as const;

/**
 * 코너로지스 상품 반출 상태 필터 리스트
 */
export const 상품_반출_상태_필터_리스트: Array<SelectOption<(typeof 상품_반출_상태_리스트)[number]>> = [
  { label: '신청 완료 (반출비용 결제대기)', value: 'PENDING_PAYMENT' },
  { label: '반출비용 결제완료', value: 'PAYMENT_COMPLETED' },
  { label: '반출 진행중', value: 'IN_PROGRESS' },
  { label: '반출 완료', value: 'COMPLETED' },
  { label: '반출 취소', value: 'CANCELLED' },
];
